/**
 * ServiceMappingPanel - Display service mapping guidance and suggestions
 * 
 * Shows:
 * - Job guide notes for advisors
 * - Suggested parts with placeholders
 * - Suggested labour with placeholders
 * 
 * Works for all service items, not just oil service.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  BookOpen, 
  Package, 
  Wrench, 
  CheckCircle2, 
  AlertTriangle,
  Plus,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ServiceMappingPanel = ({ 
  serviceItemId, 
  categoryId,
  vehicleInfo,
  onAddPart,
  onAddLabour,
  compact = false
}) => {
  const [mapping, setMapping] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(!compact);
  
  useEffect(() => {
    const fetchMapping = async () => {
      if (!serviceItemId) return;
      
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/api/service-mappings/${serviceItemId}`, {
          params: {
            vehicle_make: vehicleInfo?.make,
            vehicle_model: vehicleInfo?.model,
            fuel_type: vehicleInfo?.fuel_type,
            year: vehicleInfo?.year
          }
        });
        setMapping(res.data);
      } catch (err) {
        console.error('Failed to fetch service mapping:', err);
        setMapping(null);
      }
      setIsLoading(false);
    };
    
    fetchMapping();
  }, [serviceItemId, vehicleInfo]);
  
  if (isLoading) {
    return null; // Don't show loading state - content loads quickly
  }
  
  if (!mapping) {
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded">
        <div className="flex items-center gap-2 text-amber-700 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>No mapping configured for this service item</span>
        </div>
        <p className="text-xs text-amber-600 mt-1">
          Generic Default Mapping not available. Contact admin to add mapping.
        </p>
      </div>
    );
  }
  
  const isGeneric = mapping.mapping_type === 'generic';
  
  return (
    <Card className="border-slate-200">
      <CardHeader 
        className="py-2 px-3 bg-slate-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-slate-600" />
            Job Intelligence
            {isGeneric && (
              <Badge variant="outline" className="text-xs bg-slate-100">
                Generic Default
              </Badge>
            )}
          </CardTitle>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="py-3 space-y-4">
          {/* Job Guide */}
          {mapping.job_guide && mapping.job_guide.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Advisor Guide
              </h4>
              <ul className="space-y-1.5">
                {mapping.job_guide.sort((a, b) => a.order - b.order).map((guide, idx) => (
                  <li 
                    key={idx}
                    className={`flex items-start gap-2 text-sm ${
                      guide.is_warning 
                        ? 'text-amber-700 bg-amber-50 p-1.5 rounded border-l-2 border-amber-400' 
                        : 'text-slate-600'
                    }`}
                  >
                    {guide.is_warning ? (
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />
                    )}
                    <span>{guide.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Suggested Parts */}
          {mapping.suggested_parts && mapping.suggested_parts.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Package className="w-3 h-3" />
                Suggested Parts
              </h4>
              <div className="space-y-1">
                {mapping.suggested_parts.map((part, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-white border rounded text-sm hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      {part.is_required ? (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Required</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Optional</Badge>
                      )}
                      <div>
                        <span className="font-medium">{part.name}</span>
                        {part.quantity > 1 && (
                          <span className="text-slate-500 ml-1">x{part.quantity}</span>
                        )}
                        {part.description && (
                          <p className="text-xs text-slate-500">{part.description}</p>
                        )}
                      </div>
                    </div>
                    {onAddPart && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => onAddPart(part)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Suggested Labour */}
          {mapping.suggested_labour && mapping.suggested_labour.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Wrench className="w-3 h-3" />
                Suggested Labour
              </h4>
              <div className="space-y-1">
                {mapping.suggested_labour.map((labour, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-white border rounded text-sm hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      {labour.is_required ? (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">Required</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Optional</Badge>
                      )}
                      <div>
                        <span className="font-medium">{labour.description}</span>
                        {labour.hours && (
                          <p className="text-xs text-slate-500">{labour.hours} hours</p>
                        )}
                      </div>
                    </div>
                    {onAddLabour && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => onAddLabour(labour)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Mapping info footer */}
          {isGeneric && (
            <p className="text-xs text-slate-400 pt-2 border-t">
              This is a Generic Default Mapping. Admins can create specific mappings for this vehicle type.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default ServiceMappingPanel;
